import styled from '@emotion/styled';
import { clamp } from '@racemap/utilities/math';
import { useDrag } from '@use-gesture/react';
import { animate, type HTMLMotionProps, motion, useMotionValue, useTransform } from 'motion/react';
import { type FC, useEffect, useState } from 'react';
import { above, Breakpoint, upTo } from 'src/lib/customHooks/useBreakpoint';
import ConnectedPlayerSidebar from './Sidebar/ConnectedPlayerSidebar';

/** The width above which the sidebar sits beside the map. Mirrors the CSS below. */
export const SIDEBAR_WIDTH_BREAKPOINT = Breakpoint.phone;

const SidebarContainer = styled(motion.div)`
  top: 0;
  left: 0;
  height: 100%;
  background: white;
  z-index: 300;
  overflow: hidden;
  border-right: 1px solid var(--base-color-dark);
  transform: translateZ(0);

  @media screen and ${upTo('phone')} {
    position: absolute;
    top: 0;
    left: 0;
    right: 50px;
    bottom: 0;
    background: white;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border-right: none;
    z-index: 1000;
  }
`;

const SidebarBackdrop = styled(motion.div)`
  @media screen and ${above('phone')} {
    display: none;
  }

  @media screen and ${upTo('phone')} {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
    touch-action: none;
    backdrop-filter: brightness(1);
    -webkit-backdrop-filter: brightness(1);
  }
`;

interface Props {
  closed: boolean;
  onClose: () => void;
}

export const PlayerSidebarWrapper: FC<Props> = ({ closed, onClose }) => {
  const [showContent, setShowContent] = useState(!closed);
  const windowWidth = window.innerWidth;
  const sidebarWidth =
    windowWidth > SIDEBAR_WIDTH_BREAKPOINT ? clamp(300, windowWidth * 0.3, 400) : windowWidth - 50;
  const isWide = windowWidth > SIDEBAR_WIDTH_BREAKPOINT;
  const x = useMotionValue(closed ? -sidebarWidth : 0);

  const width = useTransform(x, (v) => sidebarWidth + v);
  const brightness = useTransform(
    x,
    (v) => `brightness(${v === -sidebarWidth ? 1 : Math.min(1, -0.7 * (v / sidebarWidth) + 0.3)})`,
  );

  const animateX = (to: number) =>
    animate(x, to, { type: 'spring', stiffness: 170, damping: 26 }).then(() => {
      setShowContent(x.get() > -sidebarWidth);
    });

  useEffect(() => {
    if (closed) {
      animateX(-sidebarWidth);
    } else {
      animateX(0);
      setShowContent(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closed]);

  const bind = useDrag(({ down, last, tap, movement: [mx], event }) => {
    event.preventDefault();
    if (tap) {
      onClose();
      return;
    }
    if (last) {
      if (mx < -sidebarWidth / 2) {
        onClose();
        return;
      }
    }
    // `immediate` while dragging == write the value straight through, no animation.
    if (down) x.set(Math.min(mx, 0));
    else animateX(0);
  }, {});

  // @use-gesture hands back a bag of DOM event handlers. motion re-types several props of
  // the same name (onDrag*, onAnimation*) for its own gesture/animation API, so the bag
  // needs a cast to be spread onto a motion element. Only pointer handlers are ever
  // actually set at runtime.
  const gesture = bind() as HTMLMotionProps<'div'>;

  return (
    <>
      <SidebarContainer
        className="side-bar-container"
        style={{
          width: isWide ? width : undefined,
          x: isWide ? undefined : x,
        }}
        onTransitionEnd={() => {
          setShowContent(!closed);
        }}
      >
        {showContent && (
          <div style={{ width: sidebarWidth, height: '100%' }}>
            <ConnectedPlayerSidebar onClose={onClose} />
          </div>
        )}
      </SidebarContainer>
      <SidebarBackdrop
        className="side-bar-backdrop"
        style={{
          pointerEvents: closed ? 'none' : 'auto',
          backdropFilter: brightness,
          WebkitBackdropFilter: brightness,
        }}
        {...gesture}
      />
    </>
  );
};
